import './tooltip.scss';

import {
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { Button, ButtonIcon } from '../button/button';

type TooltipState = {
  left: number;
  top: number;
};

type TooltipMessageProps = PropsWithChildren<{
  state: TooltipState;
  error?: boolean;
}>;

const TooltipMessage = forwardRef(
  (
    { state, error, children }: TooltipMessageProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const container = document.getElementById('portal-container');

    if (!container) return null;

    return ReactDOM.createPortal(
      <div
        className={`tooltip-message${error ? ' tooltip-message--error' : ''}`}
        style={{ top: `${state.top}px`, left: `${state.left}px` }}
        ref={ref}
      >
        <div className="tooltip-message__content">{children}</div>
      </div>,
      container
    );
  }
);

TooltipMessage.displayName = 'TooltipMessage';

type TooltipProps = PropsWithChildren<{
  message: JSX.Element | string;
  disabled?: boolean;
  error?: boolean;
}>;

export function Tooltip({ message, disabled, error, children }: TooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<TooltipState | false>(false);

  const onShowTooltip = useCallback(() => {
    if (!disabled && tooltipRef.current) {
      const position = tooltipRef.current.getBoundingClientRect();

      setState({
        left: position.x + position.width / 2,
        top: position.y,
      });
    }
  }, [disabled]);

  const onHideTooltip = useCallback(() => {
    setState(false);
  }, []);

  const onHideTooltipByMouse = useCallback((e: MouseEvent) => {
    if (tooltipRef.current && messageRef.current) {
      if (
        !tooltipRef.current.contains(e.target as HTMLElement) &&
        !messageRef.current.contains(e.target as HTMLElement)
      ) {
        setState(false);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onHideTooltip, true);
    document.addEventListener('touchmove', onHideTooltip, true);
    document.addEventListener('wheel', onHideTooltipByMouse, true);
    document.addEventListener('mousemove', onHideTooltipByMouse, true);

    return () => {
      window.removeEventListener('resize', onHideTooltip, true);
      document.removeEventListener('touchmove', onHideTooltip, true);
      document.removeEventListener('wheel', onHideTooltipByMouse, true);
      document.removeEventListener('mousemove', onHideTooltipByMouse, true);
    };
  }, [onHideTooltip, onHideTooltipByMouse]);

  return (
    <>
      <div
        className="tooltip"
        ref={tooltipRef}
        onMouseOver={onShowTooltip}
        onFocus={onShowTooltip}
        // onMouseOut={onHideTooltip}
        // onBlur={onHideTooltip}
      >
        {children ?? <Button icon={ButtonIcon.INFO} />}
      </div>

      {!disabled && state && (
        <TooltipMessage error={error} state={state} ref={messageRef}>
          {message}
        </TooltipMessage>
      )}
    </>
  );
}
